import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EditDialogComponent } from '../../../core/components/editDialog/editDialogComponent';
import { EditDialogInstance } from '../../../core/components/editDialog/editDialogService';
import { CommandTemplate } from '../../../core/entities/commandTemplate/commandTemplate';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

/**
 * One protocol of the task as the server delivers it in
 * `customFlowSettings.protocols` (#875, Lockstep mit Server #855).
 */
export interface SplitTaskProtocol {
  id: string;
  description?: string | null;
  resourceNumber?: string | null;
  resourceName?: string | null;
  state?: 'NEW' | 'PREPARED' | 'OPEN' | 'COMPLETED' | 'UPLOADING' | 'ARCHIVED';
}

/** Result of the dialog: the selected protocols, each split off onto a task of its own. */
export interface SplitTaskDialogResult {
  protocolIds: string[];
}

/**
 * Dialog for splitting a task: a task carries N protocols, and the user picks
 * the protocols to move onto tasks of their own. The unselected ones stay on
 * the original task; selecting all of them splits the task completely.
 *
 * The rows come straight from `customFlowSettings.protocols` in the order the
 * server delivers them. The key is missing when the command is disabled for
 * the task, so the dialog then shows an empty state and cannot be confirmed.
 */
@Component({
  // tslint:disable-next-line:component-selector
  selector: "app-split-task-dialog",
  templateUrl: "split-task-dialog.html",
  styleUrls: ["./split-task-dialog.scss"]
})
export class SplitTaskDialogComponent implements EditDialogInstance {

  splitTaskForm: FormGroup;
  commandTemplate: CommandTemplate;
  protocols: SplitTaskProtocol[];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    private translate: TranslateService
  ) {
    this.commandTemplate = this.config?.data?.commandTemplate;

    const protocols = this.commandTemplate?.flow?.settings?.customFlowSettings?.protocols;
    this.protocols = Array.isArray(protocols) ? protocols : [];

    this.splitTaskForm = this.fb.group({
      protocolIds: [[] as string[], Validators.required]
    });
  }

  /** description, else "<Nummer> / <Name>" of the resource, else a neutral placeholder. */
  protocolLabel(protocol: SplitTaskProtocol): string {
    if (protocol.description) {
      return protocol.description;
    }
    const resource = [protocol.resourceNumber, protocol.resourceName].filter(part => !!part).join(' / ');
    return resource || this.translate.instant('sharedModule.splitTaskDialogComponent.noResource');
  }

  getEditForm(): FormGroup {
    return this.splitTaskForm;
  }

  onConfirm(editDialogComponent: EditDialogComponent<any>): void {
    const result: SplitTaskDialogResult = { protocolIds: this.splitTaskForm.value.protocolIds };
    this.ref.close(result);
  }

  onReject(editDialogComponent: EditDialogComponent<any>): void {
    this.ref.close(false);
  }

}
